import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { ProductThumbnail } from "@/components/shared/product-thumbnail";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  AmazonProductLink,
  EligibilityCheckRetry,
  PaginationFooter,
  PaneHeading,
  ProductRowsSkeleton,
} from "./PickerParts";
import {
  cannotAddListing,
  displayedInventoryQuantity,
  eligibilityExplanation,
  formatListingPrice,
  listingBlockedReason,
  type ProductInventoryGate,
  selectedProductFromListing,
} from "./product-listing-helpers";
import type { ProductListingBrowse } from "./use-product-listing-browse";
import type { ProductSelection } from "./use-product-selection";

interface SkuListingPaneProps {
  browse: ProductListingBrowse;
  selection: ProductSelection;
  adProduct?: "sp";
  marketplaceId: string;
  inventory: ProductInventoryGate;
}

/** Middle pane of the three-pane layout: SKUs of the active group with their status badges. */
export function SkuListingPane({
  browse,
  selection,
  adProduct,
  marketplaceId,
  inventory,
}: SkuListingPaneProps) {
  const { t, i18n } = useTranslation("campaignCreation");
  const { groups, listings, activeGroup, vendor } = browse;
  const { selectedByKey } = selection;
  return (
    <section
      className="flex min-w-0 flex-col overflow-hidden rounded-[10px] border border-line bg-surface"
      aria-label={t(vendor ? "products.asinPane" : "products.skuPane")}
    >
      <PaneHeading title={t(vendor ? "products.asinColumn" : "products.sku")} />
      {listings.isError ? (
        <ErrorState message={t("products.loadError")} onRetry={() => void listings.refetch()} />
      ) : activeGroup && (listings.isPending || listings.isPlaceholderData) ? (
        <ProductRowsSkeleton label={t("products.loading")} />
      ) : !activeGroup || listings.data?.data.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">{t("products.noMatches")}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto paper-scrollbar">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-9" />
            <col className="w-16" />
            <col />
          </colgroup>
          <TableBody>
            {!listings.isPlaceholderData &&
              listings.data?.data.map((listing) => {
                const inventoryQuantity = displayedInventoryQuantity(listing);
                const price = formatListingPrice(
                  listing.priceAmount,
                  listing.priceCurrency,
                  i18n.language,
                );
                const blockedReason = listingBlockedReason(listing, inventory);
                const selectedRow = selectedByKey.has(listing.listingKey);
                return (
                  <TableRow key={listing.listingKey} aria-selected={selectedRow}>
                    <TableCell className="w-9 py-4">
                      <Checkbox
                        aria-label={
                          listing.sku
                            ? t("products.selectSku", { sku: listing.sku })
                            : t("products.selectAsin", { asin: listing.asin })
                        }
                        checked={selectedRow}
                        disabled={
                          (selection.bulkPending && !selectedRow) ||
                          cannotAddListing(listing, adProduct, inventory) ||
                          listings.isError ||
                          listings.isPlaceholderData ||
                          groups.isError ||
                          groups.isPlaceholderData ||
                          browse.searchSettling
                        }
                        onCheckedChange={(checked) => {
                          if (checked)
                            selection.addProducts([selectedProductFromListing(listing)], "search");
                          else selection.removeKeys([listing.listingKey]);
                        }}
                      />
                    </TableCell>
                    <TableCell className="w-16 py-4 pr-0">
                      <ProductThumbnail
                        imageUrl={listing.imageUrl}
                        label={listing.title ?? listing.asin}
                        asin={listing.asin}
                        sku={listing.sku ?? undefined}
                        productStatus={listing.productStatus}
                      />
                    </TableCell>
                    <TableCell className="min-w-0 overflow-hidden whitespace-normal py-4">
                      <p className="flex min-w-0 items-center gap-1.5">
                        <span className="truncate text-sm font-medium leading-5">
                          {listing.title ?? t("products.noTitle")}
                        </span>
                        {listing.productStatus === "advertised" ? (
                          <Badge variant="secondary" size="compact" className="shrink-0">
                            {t("products.status.advertised")}
                          </Badge>
                        ) : null}
                      </p>
                      <p className="mt-0.5 flex min-w-0 items-center gap-1 text-xs leading-[18px] text-muted-foreground">
                        <span className="shrink-0 font-mono font-semibold text-foreground">
                          {price ?? t("products.priceUnknown")}
                        </span>
                        {vendor ? null : (
                          <>
                            <span aria-hidden="true">|</span>
                            <span className="truncate">
                              {inventoryQuantity ?? t("products.inventoryUnknown")}
                            </span>
                          </>
                        )}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {listing.fulfillmentChannel && !vendor ? (
                          <Badge variant="outline">
                            {t(`products.fulfillment.${listing.fulfillmentChannel}`)}
                          </Badge>
                        ) : null}
                        {listing.listingStatus && listing.listingStatus.length > 0 ? (
                          <>
                            <Badge variant="secondary">
                              {t(
                                listing.listingStatus.includes("BUYABLE")
                                  ? "products.listingStatus.buyable"
                                  : "products.listingStatus.notBuyable",
                              )}
                            </Badge>
                            <Badge variant="secondary">
                              {t(
                                listing.listingStatus.includes("DISCOVERABLE")
                                  ? "products.listingStatus.discoverable"
                                  : "products.listingStatus.notDiscoverable",
                              )}
                            </Badge>
                          </>
                        ) : null}
                        {!listing.listingStatus?.length && !vendor ? (
                          <Badge variant="outline">{t("products.listingStatus.unknown")}</Badge>
                        ) : null}
                        {listing.eligibility === "eligible_with_warning" ||
                        listing.eligibility === "ineligible" ? (
                          <p
                            className={
                              listing.eligibility === "ineligible"
                                ? "text-xs text-destructive"
                                : "text-xs text-warning"
                            }
                          >
                            {eligibilityExplanation(listing, t) ??
                              t(`products.adEligibility.${listing.eligibility}`)}
                          </p>
                        ) : null}
                        {adProduct === "sp" && listing.eligibilityError === "not_returned" ? (
                          <EligibilityCheckRetry
                            pending={listings.isFetching}
                            onRetry={() => void listings.refetch()}
                          />
                        ) : null}
                        {blockedReason && listing.eligibility !== "ineligible" ? (
                          <Badge variant="secondary">
                            {t(`products.selectionBlocked.${blockedReason}`)}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1 font-mono text-xs leading-[18px] text-muted-foreground">
                        <span className="shrink-0">ASIN: {listing.asin}</span>
                        <AmazonProductLink asin={listing.asin} marketplaceId={marketplaceId} />
                        {listing.sku && !vendor ? (
                          <>
                            <span aria-hidden="true">|</span>
                            <span className="truncate">SKU: {listing.sku}</span>
                          </>
                        ) : null}
                      </p>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      <PaginationFooter
        page={browse.listingPage}
        total={listings.data?.pagination.total ?? 0}
        pageSize={listings.data?.pagination.pageSize ?? 20}
        onPageChange={browse.setListingPage}
      />
    </section>
  );
}
